import { useEffect, useRef, useState, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragMoveEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Modal from "../Modal";
import { useTheme } from "../../../contexts/Theme/ThemeContext";
import { produtosApi } from "../../../hooks/produtosApi";
import { Categoria } from "../../../types/Categoria";
import { ContainerModalOrdenacao } from "./styles";
import { Button } from "../../Button";
import { ReactComponent as Voltar } from "../../../assets/newImages/icons/angle-down.svg";
import Titulo from "../ModaisConfigsPrimeiroAcesso/Titulo/Titulo";

interface ModalOrdenacaoCategoriasProps {
  isOpen: boolean;
  setModal: (value: boolean) => void;
  ordenacaoAtual?: { id: string; nome: string }[];
  onSave?: (categorias: Categoria[]) => void;
}

interface SortableItemProps {
  id: string;
  categoria: Categoria;
}

const SortableItem: React.FC<SortableItemProps> = ({ id, categoria }) => {
  const { theme } = useTheme();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`sortable-item ${isDragging ? "dragging" : ""}`}
    >
      <div className="drag-handle" {...attributes} {...listeners}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="7" cy="5" r="1.5" fill={theme.colors.tertiary} />
          <circle cx="13" cy="5" r="1.5" fill={theme.colors.tertiary} />
          <circle cx="7" cy="10" r="1.5" fill={theme.colors.tertiary} />
          <circle cx="13" cy="10" r="1.5" fill={theme.colors.tertiary} />
          <circle cx="7" cy="15" r="1.5" fill={theme.colors.tertiary} />
          <circle cx="13" cy="15" r="1.5" fill={theme.colors.tertiary} />
        </svg>
      </div>
      <span className="categoria-nome fonteTexto">{categoria.descricao}</span>
    </div>
  );
};

export const ModalOrdenacaoCategorias: React.FC<ModalOrdenacaoCategoriasProps> = ({
  isOpen,
  setModal,
  ordenacaoAtual,
  onSave,
}) => {
  const { theme } = useTheme();
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const maxScrollRef = useRef<number>(0);

  const handleDragStart = () => {
    if (containerRef.current) {
      maxScrollRef.current = containerRef.current.scrollHeight - containerRef.current.clientHeight;
    }
  };

  const handleDragMove = useCallback((event: DragMoveEvent) => {
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const pointerY = (event.activatorEvent as PointerEvent)?.clientY + (event.delta?.y || 0);

    if (!pointerY) return;

    const threshold = 60;
    const scrollSpeed = 8;
    const maxScroll = maxScrollRef.current;
    const currentScroll = container.scrollTop;

    // Scroll para cima - só se não estiver no topo
    if (pointerY < rect.top + threshold && currentScroll > 0) {
      container.scrollTop = Math.max(0, currentScroll - scrollSpeed);
    }
    // Scroll para baixo - só se não estiver no fundo
    else if (pointerY > rect.bottom - threshold && currentScroll < maxScroll) {
      container.scrollTop = Math.min(maxScroll, currentScroll + scrollSpeed);
    }
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchCategorias = async () => {
    setLoading(true);
    try {
      const response = await produtosApi().getCategorias();
      // Filtrar apenas categorias pai (sem subcategorias)
      const categoriasPai = response.filter((c: Categoria) => Number(c.id_pasta_pai) === 0);

      if (ordenacaoAtual?.length) {
        const ordemMap = new Map(ordenacaoAtual.map((item, index) => [String(item.id), index]));
        const categoriasOrdenadas = [...categoriasPai].sort((a, b) => {
          const indexA = ordemMap.get(String(a.id)) ?? Infinity;
          const indexB = ordemMap.get(String(b.id)) ?? Infinity;
          return indexA - indexB;
        });
        setCategorias(categoriasOrdenadas);
      } else {
        setCategorias(categoriasPai);
      }
    } catch (error) {
      console.error("Erro ao buscar categorias:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchCategorias();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, ordenacaoAtual]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setCategorias((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSave = () => {
    onSave?.(categorias);
    setModal(false);
  };

  return (
    <Modal isOpen={isOpen} setModal={setModal} width="500px" maxWidth="90vw">
      <ContainerModalOrdenacao theme={theme}>
        <div className="modal-header1">
          <Titulo
            titulo="Ordenação de categorias"
            subtitulo=""
            />
          <span className="texto fonteTexto">
            Defina a ordem das categorias arrastando os itens. As que ficam no topo da lista aparece primeiro no seu catálogo.
          </span>
        </div>
        {loading ? (
          <div className="loading fonteTexto">Carregando categorias...</div>
        ) : categorias.length === 0 ? (
          <div className="empty fonteTexto">Nenhuma categoria encontrada</div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragMove={handleDragMove}
            onDragEnd={handleDragEnd}
            autoScroll={false}
          >
            <SortableContext
              items={categorias.map((cat) => cat.id)}
              strategy={verticalListSortingStrategy}
            >
              <div ref={containerRef} className="categorias-list scrollBonito">
                {categorias.map((categoria) => (
                  <div key={categoria.id} className="linha">
                    <SortableItem id={categoria.id} categoria={categoria} />
                  </div>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
        <div className="botoes">
          <div className="cancelar fonteTexto" onClick={() => setModal(false)}>
            <Voltar /> Voltar
          </div>
          <Button
            title="Salvar"
            onClick={handleSave}
            width="50%"
            className={`fonteTexto ${(loading || categorias.length === 0) && "disabled"}`}
          />
        </div>
      </ContainerModalOrdenacao>
    </Modal>
  );
};
